import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, Injector, input, output, signal, untracked } from '@angular/core';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { type DragData, dragDataTypeAllowed, DragDataTypes, dndStitchPredicate } from '@modules/drag-n-drop/types/drag-data';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { QuickActionButtonComponent } from '@modules/elements/components/quick-forms/quick-action-button/quick-action-button.component';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { injectBoardStitchyCreate, STITCHY_CREATE_UNAVAILABLE } from '../../shared/board-stitchy-create';
import { firstValueFrom, Subject } from 'rxjs';
import { BoardConversionClient } from '../../../native-actions/board-conversion.client';
import { extractSourceContent, placeSourceCard } from '../../../native-actions/drag-create-content';
import { DragCreateFlow, type DragCreateOutcome, type DragEntry } from '../../../native-actions/drag-create.flow';
import { executeInRuntime } from '../../../native-actions/drag-create.runtime';
import { BoardContentFlushService } from '../../../services/board-content-flush.service';
import { BoardsApiService, newCommandUid } from '../../../services/boards-api.service';
import { BoardsDocumentApiService } from '../../../services/boards-document-api.service';
import { BoardsDialogService } from '../../../services/boards-dialog.service';
import { BoardsStore } from '../../../services/boards.store';
import { BoardCreateOptionsComponent } from '../board-create-options/board-create-options.component';
import { NewBoardComponent } from '../new-board/new-board.component';

/**
 * The "New board" control of the rail, the list header and a folder's menu: Blank, From a template, Import and Create with Stitchy. Adapted
 * from `PanelDeckCreateComponent`. Every route ends in the same `created(id)` callback, once, for the one board that was
 * made; a repeated click while a creation is under way starts nothing (`busy`, and the store's keyed create). The menu
 * is closed by `menuChosen` before the creation starts, and the creation does not depend on the menu staying open.
 * Drag to create (R07, PAR-14) is `DragCreateFlow`: a drop of saved items makes one Board per item (a Board is copied) with
 * the captured folder, a direct Stitch to its source and, for other items, the editable source card; a failure offers
 * Retry (the same command) or Cancel remaining items. Create with Stitchy is Slides' own flow (`PanelDeckCreateComponent.blank(true)`): a prompt, one blank board through the same guarded
 * create, THEN the request goes to the host's Stitchy hand-off (`BOARD_STITCHY_CREATE`, a seam: Boards may not import the chat panel). Disabled with its reason until provided.
 */
@Component({
  selector: 'app-panel-board-create',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './panel-board-create.component.html',
  imports: [QuickActionButtonComponent, ContextMenuItemComponent, ContextMenuWindowComponent, PopoverDirective, DragNDropModule, Button2Component, ReactiveFormsModule],
})
export class PanelBoardCreateComponent {
  public readonly folderId = input<string | null>(null);
  /** Awaited before anything is created (a host's unsaved-draft check); `false` cancels the creation. */
  public readonly beforeCreate = input<() => Promise<boolean>>(async () => true);
  public readonly created = output<string>();

  private readonly injector = inject(Injector);
  private readonly store = inject(BoardsStore);
  private readonly context = inject(SpaceContextStore);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly busy = signal(false);
  protected readonly error = signal('');
  protected readonly dragOver = signal(false);
  protected readonly canRetry = signal(false);
  protected readonly canKeep = signal(false);
  protected readonly notice = signal('');
  protected readonly aiForm = signal(false);
  protected readonly stitchyCreate = injectBoardStitchyCreate();
  protected readonly stitchyUnavailable = STITCHY_CREATE_UNAVAILABLE;
  protected readonly prompt = new FormControl('', { nonNullable: true });
  protected readonly dragBusy = signal(false);
  private flow: DragCreateFlow | undefined;
  private flowSpace: string | null | undefined;
  /** Closes the "New board" menu on a choice (its items cannot use `[close]`, which would close a surrounding folder menu). */
  protected readonly menuChosen = new Subject<void>();
  private preparing = false;

  public constructor() {
    effect(() => {
      this.context.contextId();
      untracked(() => {
        this.error.set('');
        this.flow = undefined;
        this.canRetry.set(false);
        this.aiForm.set(false);
        this.prompt.reset();
      });
    });
  }

  /** A drop is offered when idle and every member is a native record (validated again, as a whole, when dropped). */
  protected readonly accepts = (data: DragData): boolean =>
    !this.busy() && !this.dragBusy() && !this.canRetry() && !!data?.data.length &&
    (data.type === DragDataTypes.board || (dragDataTypeAllowed(data.type) && dndStitchPredicate(data.type)));

  protected async drop(data: DragData): Promise<void> {
    this.dragOver.set(false);
    if (!this.accepts(data) || this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    const space = this.context.contextId();
    const folder = this.folderId();
    if (!(await this.prepare(space, folder))) return;
    this.flowSpace = space;
    this.flow = this.createFlow();
    const flow = this.flow;
    await this.settle(() => flow.drop(data.data.map(toDragEntry), folder));
  }

  protected async retryDrag(): Promise<void> {
    if (this.flow) await this.settle(() => this.flow!.retry());
  }

  protected async keepDrag(): Promise<void> {
    if (this.flow) await this.settle(() => this.flow!.keepWithoutLink());
  }

  /** The flow ends only when the server acknowledged the cancellation; an unknown answer keeps Retry and Cancel on screen. */
  protected async cancelDrag(): Promise<void> {
    if (this.flow) await this.settle(() => this.flow!.cancel());
  }

  /**
   * Runs one flow step and ALWAYS re-reads the flow's recovery state afterwards, whatever happened (a rejection included), so
   * Retry can never be hidden while a created Board still needs finishing, and a new drop cannot replace the flow meanwhile.
   */
  private async settle(step: () => Promise<DragCreateOutcome>): Promise<void> {
    this.dragBusy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const outcome = await step();
      if (outcome.status === 'stale' || this.destroyRef.destroyed) return;
      if (outcome.status === 'done') {
        this.created.emit(outcome.boardUids[0]);
        // The window moves to the new Board, so a member left out is named in a toast that outlives this block.
        if (outcome.skipped) this.injector.get(ToasterService).show({ text: outcome.message });
      } else if (outcome.status === 'cancelled') this.notice.set(outcome.message);
      else this.error.set(outcome.message);
    } catch {
      this.error.set('Something went wrong. Retry to continue.');
    } finally {
      if (!this.destroyRef.destroyed) {
        this.canRetry.set(!!this.flow?.canRetry);
        this.canKeep.set(!!this.flow?.linkRefused);
        if (!this.flow?.canRetry) this.flow = undefined;
      }
      this.dragBusy.set(false);
    }
  }

  private createFlow(): DragCreateFlow {
    const api = this.injector.get(BoardsApiService);
    const documents = this.injector.get(BoardsDocumentApiService);
    const flushes = this.injector.get(BoardContentFlushService);
    const space = this.flowSpace;
    return new DragCreateFlow(
      {
        client: new BoardConversionClient(api),
        placeCard: (boardUid, source, commandUid) => placeSourceCard({ gateway: documents, execute: executeInRuntime }, boardUid, source, commandUid),
        isCurrent: () => !this.destroyRef.destroyed && this.context.contextId() === space,
        destinationAvailable: (folder) => this.destinationAvailable(folder),
        loadSource: async (itemType, id) =>
          extractSourceContent(await firstValueFrom(this.injector.get(StitchServiceFactory).getStitchItemById(itemType as never, id))),
        flushBoards: async (uids) => (await Promise.all(uids.map((uid) => flushes.flush(uid)))).every(Boolean),
        newCommandUid,
        wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
        accept: async (boardUid) => {
          const board = await firstValueFrom(api.getBoard(boardUid, { silent: true }));
          if (this.context.contextId() === space) this.store.acceptCreatedBoard(board);
        },
      },
      () => this.context.contextId() ?? '',
    );
  }

  private destinationAvailable(folder: string | null): boolean {
    return !folder || this.store.folders().some((item) => item.id === folder && !item.archived && !item.deleted);
  }

  /** The checks every route makes before it creates anything; `false` when it must not go on. */
  private async prepare(space: string | null | undefined, folder: string | null): Promise<boolean> {
    if (this.preparing || this.busy()) return false;
    this.preparing = true;
    try {
      if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return false;
      if (!(await this.beforeCreate()()) || space !== this.context.contextId()) return false;
      if (!this.destinationAvailable(folder)) {
        this.error.set('This folder is no longer available.');
        return false;
      }
      return true;
    } finally {
      this.preparing = false;
    }
  }

  protected async blank(withStitchy = false): Promise<void> {
    if (this.busy() || (withStitchy && (!this.stitchyCreate || !this.prompt.value.trim()))) return;
    const space = this.context.contextId();
    const folder = this.folderId();
    if (!(await this.prepare(space, folder)) || this.destroyRef.destroyed) return;
    this.busy.set(true);
    this.error.set('');
    try {
      const board = await firstValueFrom(this.store.createBoard({ folderId: folder, key: `blank:${folder ?? ''}` }));
      // As Slides (panel-deck-create.component.ts:125,147): a dismissed surface neither reports the board nor asks Stitchy.
      if (this.destroyRef.destroyed || space !== this.context.contextId()) return;
      if (!board) {
        this.error.set('Could not create the board. Please retry.');
        return;
      }
      this.created.emit(board.id);
      if (withStitchy) {
        this.stitchyCreate?.({ boardId: board.id, space, prompt: this.prompt.value.trim() });
        this.aiForm.set(false);
        this.prompt.reset();
      }
    } finally {
      if (!this.destroyRef.destroyed) this.busy.set(false);
    }
  }

  protected async choose(kind: 'template' | 'import'): Promise<void> {
    const folderId = this.folderId();
    const space = this.context.contextId();
    if (!(await this.prepare(space, folderId))) return;
    this.injector.get(BoardsDialogService).showModal({
      component: kind === 'import' ? NewBoardComponent : BoardCreateOptionsComponent,
      injector: this.injector,
      title: kind === 'import' ? 'Import a board' : 'From a template',
      content: {
        folderId,
        onCreated: (id: string): void => {
          if (space === this.context.contextId()) this.created.emit(id);
        },
      },
    });
  }
}

/** A dragged summary as the flow validates it; only identity, tenant and lifecycle are read from the drag, never its content. */
function toDragEntry(item: DragData['data'][number]): DragEntry {
  const record = item as unknown as Record<string, unknown>;
  const text = (key: string): string | undefined => (typeof record[key] === 'string' ? (record[key] as string) : undefined);
  return {
    itemType: text('itemType') ?? '',
    id: text('id') ?? '',
    accountId: text('accountId') ?? '',
    title: text('title') ?? '',
    deleted: record['deleted'] === true,
    ...(text('recurrenceParentId') ? { recurrenceParentId: text('recurrenceParentId') } : {}),
  };
}
